import React, { useEffect, useState } from 'react';
import { Popover, Checkbox, CircularProgress } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { addMemberGroupMembers, removeMemberGroupMembers } from '../../../../api/member-group';
import { errorMessages } from '../../../../utils/methods';
import {
  PopoverPanel,
  PopoverTitle,
  PopoverScrollArea,
  PopoverGroupRow,
  PopoverGroupName,
  PopoverGroupCount,
  PopoverFooter,
  PopoverCancelButton,
  PopoverSubmitButton,
  PopoverEmptyText,
  ModeSegment,
  ModeSegmentButton,
  ModeHint,
} from './MemberGroupModalStyled';

/**
 * 선택한 회원을 다른 그룹으로 이동하거나 복사한다.
 * 이동 = 대상 그룹에 추가한 뒤 현재 그룹에서 뺀다. 복사 = 추가만 한다.
 */
const GroupMoveCopyPopover = ({ open, anchorEl, onClose, groups, sourceGroup, memberIds, onDone }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useSelector(state => state.user.user);
  const queryClient = useQueryClient();

  const [mode, setMode] = useState('MOVE');
  const [targetIds, setTargetIds] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) return;
    setMode('MOVE');
    setTargetIds(new Set());
  }, [open]);

  // 현재 보고 있는 그룹은 대상이 될 수 없다.
  const targets = groups.filter(group => group.id !== sourceGroup?.id);

  const toggleTarget = groupId =>
    setTargetIds(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });

  const handleSubmit = async () => {
    const selected = targets.filter(group => targetIds.has(group.id));
    if (!selected.length || !memberIds.length || submitting) return;
    setSubmitting(true);
    try {
      let skipped = 0;
      // 그룹마다 나눠 보낸다. 하나라도 실패하면 원본은 건드리지 않는다(이동이 반쪽으로 끝나지 않게).
      for (const group of selected) {
        // eslint-disable-next-line no-await-in-loop
        const result = await addMemberGroupMembers(user.token, group.id, memberIds);
        skipped += result?.skippedCount ?? 0;
      }
      // 이동은 모든 대상 추가가 끝난 뒤에만 원본에서 뺀다.
      if (mode === 'MOVE') {
        await removeMemberGroupMembers(user.token, sourceGroup.id, memberIds);
      }
      const action = mode === 'MOVE' ? t('이동') : t('복사');
      const scope =
        selected.length > 1 ? `${selected.length}${t('개 그룹에')}` : selected[0].name;
      toast.success(
        skipped > 0
          ? `${scope} ${memberIds.length}${t('명')} ${action} (${t('이미 소속')} ${skipped}${t('건 제외')})`
          : `${scope} ${memberIds.length}${t('명')} ${action}`,
      );
      queryClient.invalidateQueries({ queryKey: ['memberGroups'] });
      queryClient.invalidateQueries({ queryKey: ['memberGroupMembers'] });
      queryClient.invalidateQueries({ queryKey: ['memberGroupMemberRows'] });
      queryClient.invalidateQueries({ queryKey: ['memberGroupsOfMember'] });
      onDone?.();
      onClose();
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { sx: { marginTop: '6px', borderRadius: '10px' } } }}>
      <PopoverPanel>
        <PopoverTitle>{`${memberIds.length}${t('명을 다른 그룹으로')}`}</PopoverTitle>

        <ModeSegment>
          <ModeSegmentButton isActive={mode === 'MOVE'} onClick={() => setMode('MOVE')}>
            {t('이동')}
          </ModeSegmentButton>
          <ModeSegmentButton isActive={mode === 'COPY'} onClick={() => setMode('COPY')}>
            {t('복사')}
          </ModeSegmentButton>
        </ModeSegment>
        <ModeHint>
          {mode === 'MOVE'
            ? `${sourceGroup?.name || t('현재 그룹')}${t('에서는 빠집니다')} · ${t('여러 그룹 선택 가능')}`
            : `${t('현재 그룹에도 그대로 남습니다')} · ${t('여러 그룹 선택 가능')}`}
        </ModeHint>

        <PopoverScrollArea>
          {targets.length === 0 ? (
            <PopoverEmptyText>
              {t('옮길 그룹이 없습니다')}
              <br />
              {t('그룹을 하나 더 만들어 주세요')}
            </PopoverEmptyText>
          ) : (
            targets.map(group => (
              <PopoverGroupRow key={group.id} onClick={() => toggleTarget(group.id)}>
                <Checkbox
                  size="small"
                  checked={targetIds.has(group.id)}
                  sx={{ padding: 0, color: '#BBB', '&.Mui-checked': { color: '#4AB3BC' } }}
                />
                <PopoverGroupName>{group.name}</PopoverGroupName>
                <PopoverGroupCount>
                  {group.memberCount}
                  {t('명')}
                </PopoverGroupCount>
              </PopoverGroupRow>
            ))
          )}
        </PopoverScrollArea>

        <PopoverFooter>
          <PopoverCancelButton onClick={onClose}>{t('취소')}</PopoverCancelButton>
          <PopoverSubmitButton disabled={targetIds.size === 0 || submitting} onClick={handleSubmit}>
            {submitting ? (
              <CircularProgress size={14} sx={{ color: '#fff' }} />
            ) : mode === 'MOVE' ? (
              t('이동')
            ) : (
              t('복사')
            )}
          </PopoverSubmitButton>
        </PopoverFooter>
      </PopoverPanel>
    </Popover>
  );
};

export default GroupMoveCopyPopover;
